/**
 * What the run can touch and what touching it costs: the reach of an interact,
 * the doors and their handles, the klapi, Hannu, the finish, waking up, and
 * the price of putting a fist through the pirtti's window.
 *
 * Hashed, with the two documented exceptions: `HANNU.shout` and `HANNU.voice`
 * are what he says, not what he does. See `src/config/AGENTS.md`.
 */

export const INTERACT = {
  /** Max distance from the player cylinder centre to the interactable centre. */
  maxDistance: 2.8,
  /** Player must be looking roughly at it: cos of the max angle (~65 deg). */
  minLookDot: Math.cos((65 * Math.PI) / 180),
} as const;

/**
 * ==========================================================================
 *  A DOOR IS A THING IN THE ROOM, NOT A HOLE THAT SWITCHES OFF
 * ==========================================================================
 *  A door used to be a slab in a doorway that stopped being solid once it was
 *  a fifth of the way open, and the leaf you could see swinging out of the
 *  frame was a picture of a collider that had already gone. That was cheap and
 *  it was a lie, and the lie was the interesting half: a metre and a half of
 *  timber sweeps through the room in front of every door in this building, and
 *  under the old rule you could stand in the middle of it and feel nothing.
 *
 *  The leaf is the collider now. It swings, it is solid the whole way round,
 *  and it shoves whoever is standing in its quarter circle out of the way -
 *  which is what a door does. So an inward-opening door is no longer free:
 *  press F stood square in front of it and the thing you just opened arrives
 *  where you are. You open it from the side it is *not* coming to, which for
 *  the pirtti's bedroom door means a stride to the right of the doorway, and
 *  then the opening is clear the moment the leaf has passed. See
 *  `src/world/Door.ts` for the clock and `doorLeafBoxes` for the geometry.
 *
 *  Which is also why which way a door opens is now a level-design decision
 *  with a cost attached rather than a detail of how it looks. The one at the
 *  end of the käytävä opens *out*, into the yard, and can be taken flat out;
 *  the bedroom one opens in, and has to be worked. Both are authored in
 *  `src/config/level.ts` as a hinge and a sign.
 * ==========================================================================
 */
export const DOOR = {
  /** Full swing time in seconds, and the same either way round. */
  openDuration: 0.35,
  /**
   * How many boxes the swinging leaf is chopped into.
   *
   * Collision here is axis-aligned boxes and nothing else, so a leaf at forty
   * degrees cannot be one box: the bounding box of a 1.6 m slab at 45 deg is
   * over a metre square, and a door that shoved people standing a metre clear
   * of it would be worse than one that never touched anybody. Diced along its
   * length, each piece's own bounding box is a hand's width bigger than the
   * piece - at six, about eight centimetres of it at the worst angle, and
   * nothing at all shut or fully open. The palju's rim is built out of slices
   * of a circle for exactly this reason; see `tubRim`.
   *
   * Hashed, because it is the width of the thing that pushes you.
   */
  segments: 6,
  swingAngle: Math.PI / 2,
} as const;

/**
 * The painike on a leaf, and the one shape every door in the level wears.
 *
 * A door slab with nothing on it reads as a panel leaned against a wall, and
 * the fix is not more texture - it is the one part of a door the eye actually
 * looks for. So every leaf gets the same handle: a rose against the face, a
 * neck out of it, and a lever reaching back towards the hinge, which is the
 * way a painike is fitted and the way it is pushed.
 *
 * Measured off a real one, because the sizes are the whole of it: the lever is
 * over three times the neck it stands on, and a handle drawn any chunkier than
 * this stops being ironmongery and starts being a doorknob.
 *
 * Render-only. Nothing here is stepped by the simulation or hashed into
 * `simulationFingerprint` - `DOOR` above is what a door *does*, and this is
 * only what it looks like while it does it. `INTERACT` is the reach that
 * actually opens one, and it has never known where the handle is.
 */
export const DOOR_HANDLE = {
  /** Spindle height above the floor, which is where a hand goes. */
  height: 1.05,
  /** From the edge you pull to the middle of the rose. */
  backset: 0.065,
  /** The rose: how wide it is, and how far it stands off the leaf. */
  roseRadius: 0.032,
  roseDepth: 0.012,
  /**
   * And how far into the leaf it is sunk. A disc laid flat on the face would
   * share a plane with it, and two faces on one plane flicker - the same
   * reason the boxes this replaced were buried half a centimetre in.
   */
  sink: 0.004,
  /** The neck: from the face of the rose out to the lever's own centre line. */
  neckLength: 0.05,
  neckRadius: 0.015,
  /** The lever: how far it reaches back towards the hinge, and how thick. */
  leverLength: 0.115,
  leverRadius: 0.013,
  /** Its free end, which is turned back towards the door the way they are. */
  tipRadius: 0.011,
  tipLength: 0.028,
} as const;

/**
 * ==========================================================================
 *  THE KLAPI, AND THE VERÄJÄ IT BUYS
 * ==========================================================================
 *  Hannu has been shouting '"Kimmo" hei, pist pari klapii!' since the first
 *  commit and it never cost anybody anything. Now it does: the palju yard is
 *  fenced, the way out towards the navetta is a veräjä, and the veräjä is shut
 *  until a klapi is in the pesä.
 *
 *  Four presses of F, not one. Picking a log off the kasa and putting it
 *  through the hatch are separate things you do in separate places, and
 *  splitting them is what turns "press the button at the palju" into a line
 *  you have to run: past the kasa, past the kamiina, and back.
 *
 *  Back, because they go in one at a time. The rack is on the tub's north rim
 *  and the firebox is off its east end, so a klapi is one lap of the tub's
 *  north-east corner and the pair Hannu shouts for is that lap twice - taken
 *  with the meter emptied on the turn and with the man who wants them walking
 *  into the corridor you are turning in. That is the toll: not two metres of
 *  detour, but the whole encounter, and it is priced against a hole in the
 *  fence at the north end that costs nothing but distance.
 *
 *  ------------------------------------------------------------------------
 *   WHY THE REACH IS GENEROUS AND THE AIM IS NOT
 *  ------------------------------------------------------------------------
 *  `INTERACT` is a doorknob: 2.8 m and a 65 deg cone, because reaching for a
 *  handle is something you do with your hand and your eyes. This is not that.
 *  You go past the kamiina at 30 km/h, sideways, looking at the veräjä you are
 *  heading for - so the cone is wide enough that anything not behind you
 *  counts, and the distance is what actually has to be earned. Aiming at a
 *  firebox while threading a 2.3 m lane would not be a skill test, it would be
 *  a tax on the mouse.
 * ==========================================================================
 */
export const KLAPI = {
  /**
   * How many have to be in the pesä before the veräjä moves.
   *
   * Two, because two is what Hannu shouts for and has always shouted for -
   * *pist pari klapii* - and a gate that opened on one would be the level
   * quietly disagreeing with its own only line of dialogue. They are carried
   * one at a time, so this is also the number of laps of the tub the toll
   * costs.
   */
  logsNeeded: 2,
  /**
   * How close the pile - and the hatch - have to be.
   *
   * Wide, and wide on purpose. Both of them sit against something you run
   * *past* rather than up to, so what this number has to be is "near enough
   * that the line took you there", not "near enough to touch". Three metres
   * still means the klapikasa is only in reach from the corridor along the
   * tub's north side and the hatch only from the strip east of it - the line
   * along the halkopino south of the tub reaches neither, which is what
   * decides which way round the tub a run that wants the gate goes.
   */
  reach: 3.2,
  /**
   * Cos of the widest angle that still counts (~100 deg), so the test is
   * "it is not behind you" rather than "you are looking at it".
   */
  minLookDot: Math.cos((100 * Math.PI) / 180),
  /** The veräjä's full swing, once the fire has something to burn. */
  gateOpenDuration: 0.55,
  /**
   * And when its collider goes. Later in the swing than a door's, because a
   * door is opened by the hand already on it and this one is opened from the
   * other side of the yard: it has to read as swinging *because* of the klapi,
   * not as having been open all along.
   */
  gateClearAt: 0.35,
} as const;

/**
 * Hannu. Lives in the hot tub in the yard, has soaked long enough that the skin
 * has come off, and would very much like you to put a couple more logs in the
 * kamiina - which stands on the decking off the tub's east end, out of his
 * reach and going out.
 *
 * The three radii below are one picture of his yard, all measured from the tub
 * and worth reading together: he notices you at `triggerRadius`, he is done
 * with you past `leash`, and inside `reengageRadius` he is not standing
 * anywhere - he is coming. Since the klapit go in one at a time and the pesä
 * wants two, that middle band is where the whole encounter happens.
 *
 * Fully scripted and reactive-but-deterministic: his behaviour is a pure
 * function of (simulationTime, player position), so two identical runs produce
 * two identical Hannus. He is NOT a solid collider - a moving box that can pin
 * the player against a wall would be a run-ending stuck state, and the whole
 * point of this game is that a run never gets stuck.
 */
export const HANNU = {
  /** He notices you at this distance from the tub. */
  triggerRadius: 13.0,
  /** Climbing out of the tub. */
  riseDuration: 0.7,
  /**
   * How far his yard reaches, and the *whole* of what ends the chase.
   *
   * It reads twice: he breaks off once you are further than this from his tub,
   * and he can never be further than it himself. Both halves matter - the
   * first is the rule, the second is the guarantee that dragging him in
   * circles cannot walk him out of the level.
   *
   * There used to be a clock on it as well: seven seconds and he gave up,
   * whatever you were doing. That was right when the toll was a button you
   * pressed on the way past, and wrong the moment it became two laps of the
   * tub - a timer that expires halfway through the errand turns the man you
   * owe the wood to into a spectator watching you fetch it. So he gives up on
   * distance and on nothing else: outrun him out of his yard, do not wait him
   * out inside it.
   */
  leash: 11.0,
  /** Slower than the player on purpose: he is beatable in a straight line. */
  speed: 4.3,
  /** How close he has to be to land a swipe. */
  reach: 1.75,
  attackCooldown: 1.1,
  /**
   * The swipe pushes you directly away from him. Get hit from behind and it is
   * a *boost* - baiting the swipe on the run-line is genuinely faster.
   */
  knockbackSpeed: 5.8,
  knockbackLift: 1.7,
  staggerDuration: 0.45,
  /** How long '"Kimmo" hei, pist pari klapii!' stays on screen and in the ear. */
  shoutDuration: 2.2,
  sinkDuration: 0.9,
  bodyHeight: 1.85,
  /**
   * Half a man, across the shoulders.
   *
   * It used to be the mesh's own width and nothing else. It is physics now:
   * the circle his feet keep outside of at the palju is the staves plus this,
   * so a man walks *round* his own tub with his shoulder clear of it rather
   * than through the boards. See `Hannu.ts`.
   */
  bodyRadius: 0.42,
  /**
   * Getting over the rim, either way.
   *
   * He stands up out of the water onto the staves and then hops down off them,
   * and he goes back in the same way round: a hop up onto the rim and then
   * down into the water. `hopDuration` is one of those hops and
   * `hopClearance` is how far his feet pass over the *boards* at the top of
   * it - over the rim rather than over the higher end of the hop, because the
   * rim is the thing being cleared and the two hops have their high end at
   * opposite ends.
   *
   * Both are physics, and the first one especially: it is time he is in the
   * air rather than coming at you, and it is the whole reason the man who
   * used to walk out through the side of his own tub arrives a beat later
   * than he did. `Level.test.ts` measures what that beat costs.
   */
  hopDuration: 0.42,
  hopClearance: 0.28,
  /**
   * How long the walk home is allowed to take before the tub takes him anyway.
   *
   * There is no version of this game where a man is stuck outside his own
   * palju, so the way in has a deadline on it as well as a route: run this
   * long in `returning` and he goes in through the staves, teleported onto the
   * rim and sunk from there. It is a backstop and not a behaviour - the honest
   * walk is 2.6 s from the far end of his leash and 1.2 s from the veräjä, so
   * anything that trips this is a bug and the teleport is what keeps the bug
   * from being a man standing in the yard for the rest of the run.
   *
   * It has been reachable exactly once, and `Hannu.ts` says how: the arrival
   * test rounded away at the palju's real coordinates and he never got the
   * last femtometre of the way to the rim. That one is fixed at the source.
   * This is here because the next one will not announce itself either.
   */
  returnPatience: 6.0,
  /**
   * How deep he sits in the tub, measured to his feet.
   *
   * Set from the *waterline* and not from the floor of the tub, because what
   * this number decides is where the water cuts him: at 0.6 the surface is
   * across his shoulders and his head is out of it, which is a man asleep in a
   * palju. It used to be 1.55, which put the whole of him under - and a tub
   * with a drowned man in it is not the tub the level is about. The mesh is
   * posed standing, so his feet end up under the decking; nothing can see down
   * there, because the water is opaque and it is the lid.
   *
   * `Level.test.ts` measures it against `palju-vesi` and against his head, and
   * it does that over the whole of the sink rather than at the end of it: the
   * rule is that his head is *always* above the surface, not that it comes
   * back up eventually.
   */
  submergedDepth: 0.6,
  /**
   * Where he goes when he gives up on catching you: the mouth of the veräjä,
   * which he stands in front of until somebody puts a log in his fire. He
   * paces `paceWidth` metres across it, once every `pacePeriod` seconds - a
   * triangle wave on `simulationTime`, so two identical runs still get two
   * identical Hannus.
   *
   * He is not what stops you. The veräjä is (see `KLAPI`), because a moving box
   * in a 2 m gate is a run-ending stuck state and this game does not have
   * those. He is why it is shut.
   */
  guardSpeed: 3.1,
  /** How far inside the yard he stands. The opening itself stays walkable. */
  guardStandoff: 1.15,
  paceWidth: 1.5,
  pacePeriod: 3.4,
  /**
   * And how far back into the yard you have to come before he leaves the
   * gateway to have another go at you.
   *
   * Measured from the tub, like `leash` and like `triggerRadius`, so the three
   * of them are one ruler and not three: he notices you at 13, his yard ends
   * at 11, and inside 6.5 he is not standing anywhere - he is coming. The gap
   * between this and the leash is the hysteresis, and it is what stops a man
   * who broke off at the boundary from flickering back and forth across it.
   *
   * Where it sits between those two is decided by the yard rather than by
   * feel, and `Level.test.ts` pins it: the veräjä's own post has to be
   * *outside* it, so somebody standing at the shut gate gets the man pacing in
   * front of it rather than a charge, and both klapi reaches have to be well
   * *inside* it, so fetching the second log means walking back into him. That
   * is the whole of what "Hannu häiritsee kunnes pesässä on pari klapia" is.
   */
  reengageRadius: 6.5,
  /**
   * He knows exactly who you are, and he wants two logs in the kamiina - which
   * is a fifteen-second job for somebody already out of the water, and a
   * fifteen-second job you cannot afford.
   */
  shout: '"Kimmo" hei, pist pari klapii!',
  /** Close, boiled, and out of breath: low and fast. See src/audio/Voice.ts. */
  voice: { rate: 1.25, pitch: 0.72, volume: 1.0 },
} as const;

export const FINISH = {
  /** Length of the purely cosmetic sit-down camera move. */
  sitDuration: 0.65,
} as const;

/** Render-only pre-run sequence; excluded from the simulation fingerprint. */
export const WAKE_UP = {
  duration: 1.7,
  /** Partially open during the shout, fully open only after standing up. */
  shoutEyeDuration: 1.6,
  shoutEyeOpening: 0.4,
  blurPixels: 22,
  startPitch: 0.65,
  startEyeHeight: 0.62,
  startRoll: 0.55,
} as const;

/**
 * ==========================================================================
 *  WHAT THE WINDOW COSTS ONCE THERE IS GLASS IN IT
 * ==========================================================================
 *  Going out through a closed window hurts. Kimmo yells, and for the next
 *  `duration` seconds he runs at `slowFactor` of what he would otherwise be
 *  doing - a man who has just taken a pane of glass across the shins does not
 *  land it and sprint.
 *
 *  ------------------------------------------------------------------------
 *   THIS ONE *IS* HASHED, UNLIKE EVERY OTHER THING ABOUT THE GLASS
 *  ------------------------------------------------------------------------
 *  The pane itself is decor and always was. This is not: it changes how fast
 *  the player moves, which means it changes when he arrives, which means it
 *  decides leaderboard times. So it is in `simulationFingerprint()`, and so is
 *  `LevelDefinition.ikkuna` - because *where the pane is* now decides whether
 *  anybody pays this, and a pane in a different place is a different game.
 *
 *  That is the whole difference between this block and `SHATTER` two below it.
 *  Moving a shard is free. Moving this retires the board.
 *
 *  ------------------------------------------------------------------------
 *   IT IS A CAP, NOT A BITE
 *  ------------------------------------------------------------------------
 *  `slowFactor` scales the speed he is accelerating *towards*, the way
 *  `momentum` does, rather than chopping the velocity he already has. Two
 *  reasons. The window is taken in the air, so a bite would land on whatever
 *  fraction of the dive happened to be left rather than on the run; and
 *  friction then walks him down to the new ceiling over about a third of a
 *  second, which reads as a man losing his legs under him instead of as a man
 *  hitting something. The vauhti meter is untouched - what this costs is the
 *  three seconds, and the meter keeps climbing through them.
 * ==========================================================================
 */
export const GLASS_PAIN = {
  /** Fraction of his usual speed for the duration. 0.75 = a quarter slower. */
  slowFactor: 0.75,
  /** Seconds it lasts, from the step the glass goes. */
  duration: 3.0,
} as const;
